import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const ScheduleSection = ({
  data,
  editableData,
  editMode,
  onChange,
  formatDate,
  platform,
  loading
}) => {
  const [expandedAdsets, setExpandedAdsets] = useState(false);

  const adGroups = data.ad_groups || [];
  const isCampaignLevel = platform === "google" || adGroups.length === 1;

  const renderCampaignDates = () => (
    <div className={"grid grid-cols-1 sm:grid-cols-2 gap-4" + (loading ? " disabled" : "")}>
      <div className="bg-white/10 rounded-xl p-4 border border-white/20">
        <span className="text-xs text-white/70 block mb-1.5">Fecha de Inicio</span>
        {editMode ? (
          <input
            type="date"
            value={editableData.start_date?.split('T')[0] || ''}
            onChange={e => onChange('start_date', e.target.value)}
            className="w-full border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white bg-white/10 focus:outline-none focus:ring-2 focus:ring-fuchsia-400/20 focus:border-fuchsia-400 transition-all"
          />
        ) : (
          <span className="text-sm text-white">{formatDate(data.start_date)}</span>
        )}
      </div>
      <div className="bg-white/10 rounded-xl p-4 border border-white/20">
        <span className="text-xs text-white/70 block mb-1.5">Fecha de Finalización</span>
        {editMode ? (
          <input
            type="date"
            value={editableData.end_date?.split('T')[0] || ''}
            onChange={e => onChange('end_date', e.target.value)}
            className="w-full border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white bg-white/10 focus:outline-none focus:ring-2 focus:ring-fuchsia-400/20 focus:border-fuchsia-400 transition-all"
          />
        ) : (
          <span className="text-sm text-white">{formatDate(data.end_date)}</span>
        )}
      </div>
    </div>
  );

  const renderAdsetDates = () => (
    <div className={"space-y-4" + (loading ? " disabled" : "")}>
      <div className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-3 cursor-pointer border border-white/20" onClick={() => setExpandedAdsets(!expandedAdsets)}>
        <span className="text-sm font-medium text-white">Programación por Adset</span>
        <button
          className="text-white/70 hover:text-white p-1 rounded-full hover:bg-white/20"
        >
          {expandedAdsets ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      <AnimatePresence>
        {expandedAdsets && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="space-y-4 overflow-hidden"
          >
            {adGroups.map(ag => {
              const startField = editMode ? (
                <input
                  type="date"
                  value={
                    editableData.adset_dates?.[ag.id]?.start_date?.split('T')[0] || 
                    ag.start_date?.split('T')[0] || ''
                  }
                  onChange={e => onChange('adset_dates', {
                    ...editableData.adset_dates,
                    [ag.id]: {
                      ...editableData.adset_dates?.[ag.id],
                      start_date: e.target.value,
                    }
                  })}
                  className="w-full border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white bg-white/10 focus:outline-none focus:ring-2 focus:ring-fuchsia-400/20 focus:border-fuchsia-400 transition-all"
                />
              ) : (
                formatDate(ag.start_date)
              );

              const endField = editMode ? (
                <input
                  type="date"
                  value={
                    editableData.adset_dates?.[ag.id]?.end_date?.split('T')[0] || 
                    ag.end_date?.split('T')[0] || ''
                  }
                  onChange={e => onChange('adset_dates', {
                    ...editableData.adset_dates,
                    [ag.id]: {
                      ...editableData.adset_dates?.[ag.id],
                      end_date: e.target.value,
                    }
                  })}
                  className="w-full border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white bg-white/10 focus:outline-none focus:ring-2 focus:ring-fuchsia-400/20 focus:border-fuchsia-400 transition-all"
                />
              ) : (
                formatDate(ag.end_date)
              );

              return (
                <motion.div
                  key={ag.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white/10 border border-white/20 rounded-xl p-4"
                >
                  <div className="text-sm font-medium mb-2 text-white">{ag.name}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-white/70 block mb-1.5">Inicio</span>
                      {typeof startField === 'string' ? <span className="text-sm text-white">{startField}</span> : startField}
                    </div>
                    <div>
                      <span className="text-xs text-white/70 block mb-1.5">Finalización</span>
                      {typeof endField === 'string' ? <span className="text-sm text-white">{endField}</span> : endField}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <div className="font-sans mt-6">
      <h3 className="text-sm font-medium text-white/70 mb-3">Fechas</h3>
      {isCampaignLevel ? renderCampaignDates() : renderAdsetDates()}
    </div>
  );
};

export default ScheduleSection;